// src/hooks/useCmsSearch.js

import { useState, useCallback } from "react";

const API_BASE = (
    process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/+$/, "");

export default function useCmsSearch() {

    const [results, setResults] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState(null);

    const [offset, setOffset] =
        useState(0);

    const [hasMore, setHasMore] =
        useState(true);

    const [currentQuery, setCurrentQuery] =
        useState("");

    const search = useCallback(async (
        query,
        {
            count = 10,
            offset = 0,
            seenDomains = [],
            append = false,
        } = {}
    ) => {

        setLoading(true);
        setError(null);

        if (!append) {

            setResults([]);
            setOffset(0);
            setHasMore(true);
        }

        setCurrentQuery(query);

        try {

            const response = await fetch(
                `${API_BASE}/api/cms-search/search`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        query,
                        count,
                        offset,
                        seen_domains:
                            seenDomains,
                    }),
                }
            );

            if (!response.ok) {

                throw new Error(
                    `HTTP ${response.status}`
                );
            }

            if (!response.body) {

                throw new Error(
                    "Streaming not supported"
                );
            }

            const reader =
                response.body.getReader();

            const decoder =
                new TextDecoder("utf-8");

            let buffer = "";

            let receivedCount = 0;

            let streamHasMore = false;

            while (true) {

                const {
                    done,
                    value,
                } = await reader.read();

                if (done) {
                    break;
                }

                buffer += decoder.decode(
                    value,
                    { stream: true }
                );

                const lines =
                    buffer.split("\n");

                buffer =
                    lines.pop() || "";

                for (const line of lines) {

                    if (!line.trim()) {
                        continue;
                    }

                    try {

                        const parsed =
                            JSON.parse(line);

                        if (
                            parsed.meta &&
                            parsed.meta.has_more
                        ) {

                            streamHasMore = true;
                        }

                        if (
                            parsed.success &&
                            parsed.result
                        ) {

                            receivedCount++;

                            setResults(prev => [
                                ...prev,
                                parsed.result,
                            ]);
                        }

                    } catch (err) {

                        console.error(
                            "NDJSON parse error",
                            err
                        );
                    }
                }
            }

            setHasMore(streamHasMore);

            // English comment: Move offset correctly.
            setOffset(offset + receivedCount);

        } catch (err) {

            console.error(err);

            setError(
                err.message ||
                "Search failed"
            );

        } finally {

            setLoading(false);
        }

    }, []);

    const handleLoadMore =
        useCallback(async () => {

            const nextOffset =
                offset + 10;

            // English comment: Collect already loaded domains.
            const seenDomains = results
                .map(result => {

                    try {

                        return new URL(
                            result.link
                        )
                            .hostname
                            .replace(/^www\./, "")
                            .toLowerCase();

                    } catch {

                        return null;
                    }

                })
                .filter(Boolean);

            await search(
                currentQuery,
                {
                    count: 10,
                    offset: nextOffset,
                    append: true,
                    seenDomains,
                }
            );

        }, [
            offset,
            currentQuery,
            results,
            search,
        ]);

    return {
        results,
        loading,
        error,
        search,
        hasMore,
        handleLoadMore,
    };
}